if (typeof AudioSampleButton != 'function') {
	class AudioSampleButton extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				url: 'data-sample-url',
				type: 'data-sample-type',
				playBtn: '.play-btn',
				pauseBtn: '.pause-btn',
				previewBtn: '.preview-btn',
				btnText: '.btn-text',
				metafields: 'data-metafields',
				audioTitles: 'data-audio-titles',
				pdfTitles: 'data-pdf-titles',
				audioText: 'data-audio-text',
				pdfText: 'data-pdf-text',
				errorMessage: 'data-error-message',
				bookshelf: 'data-bookshelf',
				bookId: 'data-book-id',
				btn: 'button',
			};

			this.elements = {
				url: this.getAttribute(this.selectors.url),
				type: this.getAttribute(this.selectors.type) || 'audio',
				playBtn: this.querySelector(this.selectors.playBtn),
				pauseBtn: this.querySelector(this.selectors.pauseBtn),
				previewBtn: this.querySelector(this.selectors.previewBtn),
				btnText: this.querySelector(this.selectors.btnText),
				metafields: this.getAttribute(this.selectors.metafields),
				audioTitles: this.getAttribute(this.selectors.audioTitles),
				pdfTitles: this.getAttribute(this.selectors.pdfTitles),
				audioText: this.getAttribute(this.selectors.audioText) || 'Listen to Sample',
				pdfText: this.getAttribute(this.selectors.pdfText) || 'Preview Sample',
				errorMessage: this.getAttribute(this.selectors.errorMessage),
				bookshelf: this.getAttribute(this.selectors.bookshelf),
				bookId: this.getAttribute(this.selectors.bookId),
				btn: this.querySelector(this.selectors.btn),
			};

			this.classes = {
				hidden: 'visually-hidden',
			};

			this.audio = null;

			if (!this.classList.contains(this.classes.hidden)) {
				this.initiallyVisible = true;
			} else {
				this.initiallyVisible = false;
			}
			
			this.productsArray = JSON.parse(this.elements.metafields || '[]');
			this.audioTitles = (this.elements.audioTitles || '').split(',').map((title) => title.trim().toLowerCase());
			this.pdfTitles = (this.elements.pdfTitles || '').split(',').map((title) => title.trim().toLowerCase());
			
			this.bookshelfData = [];
			try {
				this.bookshelfData = this.elements.bookshelf ? JSON.parse(this.elements.bookshelf) : [];
			} catch (e) {
				this.bookshelfData = [];
			}
			this.bookId = this.elements.bookId;

			this.init();
		}

		userOwnsBook() {
			if (!this.bookshelfData || !Array.isArray(this.bookshelfData) || !this.bookId) {
				return false;
			}
			
			return this.bookshelfData.some(book => book.bookId === this.bookId);
		}

		async init() {
			if (this.userOwnsBook()) {
				this.classList.add(this.classes.hidden);
				this.addListeners();
				return;
			}

			if (this.initiallyVisible && this.elements.url) {
				let isValidUrl = false;
				if (this.elements.type === 'audio') {
					isValidUrl = this.isValidAudioUrl(this.elements.url);
				} else if (this.elements.type === 'pdf') {
					isValidUrl = this.isValidPdfUrl(this.elements.url);
				}

				if (!isValidUrl) {
					this.classList.add(this.classes.hidden);
					this.addListeners();
					return;
				}
			} else {
				this.classList.add(this.classes.hidden);
				this.addListeners();
				return;
			}

			// Set up button appearance based on type (only reached if validation passed)
			if (this.elements.type === 'audio') {
				this.updateButtonForAudio();
			} else if (this.elements.type === 'pdf') {
				this.updateButtonForPdf();
			}

			this.addListeners();
		}

		// Validate audio URL before creating Audio object
		isValidAudioUrl(url) {
			try {
				if (!url || typeof url !== 'string') return false;

				// Check for common audio extensions
				const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'];
				const urlLower = url.toLowerCase();
				const hasValidExtension = audioExtensions.some((ext) => urlLower.endsWith(ext));

				if (!hasValidExtension) return false;

				// Must be a valid URL format
				new URL(url);
				return true;
			} catch {
				// Any error in validation means invalid URL
				return false;
			}
		}

		// Validate PDF URL before opening
		isValidPdfUrl(url) {
			try {
				if (!url || typeof url !== 'string') return false;

				// Check for common PDF extensions
				const pdfExtensions = ['.pdf'];
				const urlLower = url.toLowerCase();

				// Must have a valid PDF extension
				const hasValidExtension = pdfExtensions.some((ext) => urlLower.endsWith(ext));
				if (!hasValidExtension) return false;

				// Must be a valid URL format
				new URL(url);
				return true;
			} catch {
				return false;
			}
		}

		// Validate resource availability via network request
		async validateResource(url) {
			try {
				if (!url || typeof url !== 'string') return false;

				const response = await fetch(url, {
					method: 'HEAD',
					// Add timeout to prevent hanging requests
					signal: AbortSignal.timeout(5000),
				});

				if (!response.ok) {
					return false;
				}

				return true;
			} catch (error) {
				return false;
			}
		}

		// Show error modal when sample is not available
		showSampleNotAvailableModal() {
			// Create modal HTML
			const modalHTML = `
				<div class="sample-error-modal" style="
					position: fixed;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background: rgba(0, 0, 0, 0.5);
					z-index: 9999;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 20px;
				">
					<div class="sample-error-content" style="
						background: white;
						padding: 30px;
						border-radius: 8px;
						max-width: 500px;
						width: 100%;
						text-align: center;
						box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
					">
						<h3 style="margin-top: 0; color: #333;">Sample Not Available</h3>
						<p style="margin: 20px 0; color: #666; line-height: 1.5;">
							${this.elements.errorMessage}
						</p>
						<button class="sample-error-close button button--primary button--full">
							Close
						</button>
					</div>
				</div>
			`;

			// Add modal to DOM
			const modalElement = document.createElement('div');
			modalElement.innerHTML = modalHTML;
			const modal = modalElement.firstElementChild;
			document.body.appendChild(modal);

			// Add close functionality
			const closeButton = modal.querySelector('.sample-error-close');
			const closeModal = () => {
				modal.remove();
			};

			closeButton.addEventListener('click', closeModal);
			modal.addEventListener('click', (e) => {
				if (e.target === modal) {
					closeModal();
				}
			});

			// Close on escape key
			const handleEscape = (e) => {
				if (e.key === 'Escape') {
					closeModal();
					document.removeEventListener('keydown', handleEscape);
				}
			};
			document.addEventListener('keydown', handleEscape);

			// Auto-focus close button for accessibility
			closeButton.focus();
		}

		// Create audio object only when needed, with validation
		createAudioObject(url) {
			if (!this.isValidAudioUrl(url)) {
				return null;
			}

			try {
				const audio = new Audio(url);
				this.setupAudioErrorHandling(audio);
				return audio;
			} catch (error) {
				return null;
			}
		}

		setupAudioErrorHandling(audioElement) {
			const audio = audioElement || this.audio;
			if (!audio) return;

			// Prevent console errors by handling all audio error events
			audio.addEventListener('error', () => {
				// Silently handle audio errors to prevent console noise
				this.handleAudioError();
			});

			// Handle network errors when audio fails to load
			audio.addEventListener('stalled', () => {
				this.handleAudioError();
			});

			// Handle when audio data is corrupted or unavailable
			audio.addEventListener('loadstart', () => {
				// Set a timeout to detect if loading is taking too long
				this.loadTimeout = setTimeout(() => {
					if (audio && audio.readyState === 0) {
						this.handleAudioError();
					}
				}, 3000);
			});

			audio.addEventListener('loadeddata', () => {
				// Clear timeout if audio loads successfully
				if (this.loadTimeout) {
					clearTimeout(this.loadTimeout);
					this.loadTimeout = null;
				}
			});

			// Handle canplay event to clear timeout
			audio.addEventListener('canplay', () => {
				if (this.loadTimeout) {
					clearTimeout(this.loadTimeout);
					this.loadTimeout = null;
				}
			});
		}

		handleAudioError() {
			if (this.audio) {
				// Set to empty data URL to stop any ongoing network requests
				this.audio.src = 'data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ4AAAC/////v////7////+/////';
				this.audio.pause();
				this.audio.currentTime = 0;
			}

			// Clear any timeouts
			if (this.loadTimeout) {
				clearTimeout(this.loadTimeout);
				this.loadTimeout = null;
			}

			// Reset button state
			if (this.elements.playBtn) this.elements.playBtn.classList.remove(this.classes.hidden);
			if (this.elements.pauseBtn) this.elements.pauseBtn.classList.add(this.classes.hidden);

			if (this.elements.btnText) {
				this.elements.btnText.textContent = 'Sample Unavailable';
			}

			// Clear any loading states
			if (this.elements.btn && this.elements.btn.hasAttribute('aria-busy')) {
				this.elements.btn.removeAttribute('aria-busy');
			}
		}

		updateButtonForAudio() {
			if (this.elements.btn) {
				this.elements.btn.innerHTML = `
					<span class="loader-button__text">
						<span class="play-btn">
							<svg
								width="24"
								height="24"
								class="icon icon--player-play-fill"
								viewBox="0 0 24 24" 
								fill="currentColor"
							>
								<path stroke="none" d="M0 0h24v24H0z" fill="none" />
								<path d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z" />
							</svg>
						</span>
						<span class="pause-btn visually-hidden">
							<svg
								width="24"
								height="24"
								class="icon icon--player-pause-fill"
								viewBox="0 0 24 24" 
								fill="currentColor"
							>
								<path stroke="none" d="M0 0h24v24H0z" fill="none" />
								<path d="M9 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z" />
								<path d="M17 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z" />
							</svg>
						</span>
						<span class="btn-text" style="padding-left: 6px">
							${this.elements.audioText}
						</span>
					</span>
				`;

				// Re-query the elements after updating innerHTML
				this.elements.playBtn = this.querySelector(this.selectors.playBtn);
				this.elements.pauseBtn = this.querySelector(this.selectors.pauseBtn);
				this.elements.btnText = this.querySelector(this.selectors.btnText);
			}
		}

		updateButtonForPdf() {
			if (this.elements.btn) {
				this.elements.btn.innerHTML = `
					<span class="loader-button__text">
						<span class="preview-btn">
							<svg
								width="24"
								height="24"
								class="icon icon--file-pdf"
								viewBox="0 0 24 24" 
								fill="currentColor"
							>
								<path stroke="none" d="M0 0h24v24H0z" fill="none" />
								<path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
							</svg>
						</span>
						<span class="btn-text" style="padding-left: 6px">
							${this.elements.pdfText}
						</span>
					</span>
				`;

				// Re-query the elements after updating innerHTML
				this.elements.previewBtn = this.querySelector(this.selectors.previewBtn);
				this.elements.btnText = this.querySelector(this.selectors.btnText);

				// Clear audio-specific elements since this is now PDF
				this.elements.playBtn = null;
				this.elements.pauseBtn = null;
			}
		}

		addListeners() {
			this.addEventListener('click', () => {
				if (this.elements.type === 'audio') {
					this.handleAudioClick();
				} else if (this.elements.type === 'pdf') {
					this.handlePdfClick();
				}
			});

			document.addEventListener('variant:changed', async (event) => {
				try {
					// Handle variant changes safely using bracket notation to avoid type issues
					if (!event || typeof event !== 'object' || !('detail' in event)) {
						return;
					}

					const detail = event['detail'];
					if (!detail || typeof detail !== 'object') {
						return;
					}

					const currentVariant = detail['variant'];
					if (!currentVariant || typeof currentVariant !== 'object') {
						return;
					}

					// Reset audio if it's playing
					if (this.isPlaying()) {
						this.pauseAudio();
					}

					// Always reset button state to "Play" on variant change
					this.resetButtonState();

					// Hide component initially on variant change - only show if new variant has valid resource
					this.classList.add(this.classes.hidden);

					// Check if user owns the book - if they do, keep component hidden
					if (this.userOwnsBook()) {
						return;
					}

					// Check if variant title matches either audio or PDF titles
					const variantTitleLower = (currentVariant['title'] || '').toLowerCase();
					let isTitleAllowed = false;
					let variantType = 'audio';

					// Check PDF titles first
					for (const pdfTitle of this.pdfTitles) {
						if (variantTitleLower.includes(pdfTitle)) {
							isTitleAllowed = true;
							variantType = 'pdf';
							break;
						}
					}

					// Check audio titles if not already matched PDF
					if (!isTitleAllowed) {
						for (const audioTitle of this.audioTitles) {
							if (variantTitleLower.includes(audioTitle)) {
								isTitleAllowed = true;
								variantType = 'audio';
								break;
							}
						}
					}

					// If variant title is not allowed, hide the button
					if (!isTitleAllowed) {
						// Component is already hidden from above, just return
						return;
					}

					// Determine if the current variant is in the productsArray and if the metafield is not null
					const variantProduct = this.productsArray.find((product) => {
						return product.id === currentVariant['id'] && product.sample !== null;
					});

					// If the variant is in the array and the metafield is not null
					if (variantProduct) {
						// Update the sample URL and use the variant type we determined
						const newSampleUrl = variantProduct.sample;
						const newSampleType = variantType; // Use the type we determined from title matching

						if (newSampleUrl) {
							// Validate URL format before updating
							let isValidUrl = false;
							if (newSampleType === 'audio') {
								isValidUrl = this.isValidAudioUrl(newSampleUrl);
							} else if (newSampleType === 'pdf') {
								isValidUrl = this.isValidPdfUrl(newSampleUrl);
							}

							if (!isValidUrl) {
								return;
							}

							this.classList.remove(this.classes.hidden);

							// Update the data attributes
							this.setAttribute(this.selectors.url, newSampleUrl);
							this.setAttribute(this.selectors.type, newSampleType);
							this.elements.url = newSampleUrl;
							this.elements.type = newSampleType;

							// Update based on new type
							if (newSampleType === 'audio') {
								if (this.audio) {
									this.audio.pause();
									this.audio = null;
								}
								this.updateButtonForAudio();
							} else if (newSampleType === 'pdf') {
								// Clear any existing audio when switching to PDF
								if (this.audio) {
									this.audio.pause();
									this.audio = null;
								}
								this.updateButtonForPdf();
							}
						}
					}
				} catch (error) {
					// On any error, ensure component stays hidden
					this.classList.add(this.classes.hidden);
				}
			});
		}

		async handleAudioClick() {
			// Check if resource exists before attempting to play
			if (this.elements.url) {
				const isResourceValid = await this.validateResource(this.elements.url);
				if (!isResourceValid) {
					// Show modal instead of hiding button
					this.showSampleNotAvailableModal();
					return;
				}
			}

			// Create audio object only when user clicks (lazy loading)
			if (!this.audio && this.elements.url) {
				this.audio = this.createAudioObject(this.elements.url);
				if (!this.audio) {
					// Invalid URL or audio creation failed
					this.handleAudioError();
					return;
				}
			}

			if (!this.audio) {
				this.handleAudioError();
				return;
			}

			if (this.isPlaying()) {
				this.pauseAudio();
				this.updateButton(false);
			} else {
				this.playAudio();
				this.updateButton(true);
			}
		}

		// PDF-specific click handler
		async handlePdfClick() {
			if (!this.elements.url) {
				this.handlePdfError();
				return;
			}

			// Validate PDF URL before attempting to open
			if (!this.isValidPdfUrl(this.elements.url)) {
				this.handlePdfError();
				return;
			}

			// Check if resource exists before attempting to open
			const isResourceValid = await this.validateResource(this.elements.url);
			if (!isResourceValid) {
				// Show modal instead of generic error
				this.showSampleNotAvailableModal();
				return;
			}

			try {
				window.open(this.elements.url, '_blank');
			} catch (error) {
				this.handlePdfError();
			}
		}

		handlePdfError() {
			if (this.elements.btnText) {
				this.elements.btnText.textContent = 'Preview Unavailable';
			}

			// Clear any loading states
			if (this.elements.btn && this.elements.btn.hasAttribute('aria-busy')) {
				this.elements.btn.removeAttribute('aria-busy');
			}
		}

		playAudio() {
			if (this.audio && this.audio.paused) {
				this.audio.play().catch(() => {
					this.handleAudioError();
				});
			}
		}

		pauseAudio() {
			if (this.audio && !this.audio.paused) {
				this.audio.pause();
			}
		}

		isPlaying() {
			return this.audio && !this.audio.paused;
		}

		isPaused() {
			return !this.audio || this.audio.paused;
		}

		// Explicitly reset button to initial "Play" state
		resetButtonState() {
			if (this.elements.playBtn) {
				this.elements.playBtn.classList.remove(this.classes.hidden);
			}

			if (this.elements.pauseBtn) {
				this.elements.pauseBtn.classList.add(this.classes.hidden);
			}

			if (this.elements.btnText && this.elements.btnText.textContent) {
				// Make sure text shows "Play" regardless of current state
				const currentText = this.elements.btnText.textContent;
				if (currentText.includes('Pause')) {
					this.elements.btnText.textContent = currentText.replace('Pause ', 'Play ');
				}
			}
		}

		updateButton(isPlaying) {
			if (isPlaying) {
				if (this.elements.playBtn) {
					this.elements.playBtn.classList.add(this.classes.hidden);
				}

				if (this.elements.pauseBtn) {
					this.elements.pauseBtn.classList.remove(this.classes.hidden);
				}

				if (this.elements.btnText && this.elements.btnText.textContent) {
					this.elements.btnText.textContent = this.elements.btnText.textContent.replace('Play ', 'Pause ');
				}
			} else {
				if (this.elements.playBtn) {
					this.elements.playBtn.classList.remove(this.classes.hidden);
				}

				if (this.elements.pauseBtn) {
					this.elements.pauseBtn.classList.add(this.classes.hidden);
				}

				if (this.elements.btnText && this.elements.btnText.textContent) {
					this.elements.btnText.textContent = this.elements.btnText.textContent.replace('Pause ', 'Play ');
				}
			}
		}
	}

	customElements.define('audio-sample-button', AudioSampleButton);
}
